import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";
import { SkuCombobox } from "@/components/SkuCombobox";
import { moneyAdd, moneyMul } from "@/lib/money";

// Keep Excel-pasted unit costs as decimal strings. Never use Number for their source value.
const normalizePastedUnitCost = (value) => {
  const raw = String(value ?? "").trim().replace(/\s/g, "");
  if (raw.includes(",") && raw.includes(".")) {
    return raw.lastIndexOf(",") > raw.lastIndexOf(".")
      ? raw.replace(/\./g, "").replace(",", ".") : raw.replace(/,/g, "");
  }
  return raw.replace(",", ".");
};
const isValidUnitCost = (value) => /^(?:0|[1-9]\d*)(?:\.\d{1,12})?$/.test(String(value));

const emptyItem = { product_id: "", ordered_qty: "", purchase_unit_cost: "", repack_required: false };

export default function PurchaseForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const { user } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [purchaseNo, setPurchaseNo] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [shipping, setShipping] = useState("0");
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.filter((p) => p.status === "ACTIVE"))).catch(() => {});
    api.get("/suppliers").then(({ data }) => setSuppliers(Array.isArray(data) ? data : [])).catch(() => {});
    if (isEdit) {
      api.get(`/purchases/${id}`).then(({ data }) => {
        setPurchaseNo(data.purchase_no);
        setPurchaseDate(data.purchase_date);
        setSupplierId(data.supplier_id || "");
        setShipping(String(data.inbound_shipping_cost ?? "0"));
        if (data.status !== "DRAFT") {
          setLocked(true);
          setError("Purchase sudah OPEN dan terkunci untuk perubahan.");
        }
        setItems(data.items.map((it) => ({
          product_id: it.product_id,
          ordered_qty: String(it.ordered_qty),
          purchase_unit_cost: String(it.purchase_unit_cost),
          repack_required: !!it.repack_required,
        })));
      }).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
    }
  }, [id, isEdit]);

  const rowSubtotal = (it) => {
    const q = Number(it.ordered_qty);
    if (!Number.isSafeInteger(q) || q < 0) return "0";
    return moneyMul(it.purchase_unit_cost, q);
  };
  const total = useMemo(() => items.reduce((s, it) => moneyAdd(s, rowSubtotal(it)), "0"), [items]);
  const activeSuppliers = suppliers.filter((s) => s.status === "ACTIVE" || s.id === supplierId);

  const setItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const addRow = () => setItems((prev) => [...prev, { ...emptyItem }]);
  const removeRow = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!supplierId) {
      setError("Supplier wajib dipilih.");
      return;
    }
    if (items.some((it) => !isValidUnitCost(it.purchase_unit_cost))) {
      setError("Harga satuan harus angka desimal valid, maksimal 12 angka di belakang koma.");
      return;
    }
    if (items.some((it) => !it.product_id)) {
      setError("Setiap baris harus memilih SKU.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        purchase_date: purchaseDate,
        supplier_id: supplierId,
        inbound_shipping_cost: shipping === "" ? 0 : shipping,
        items: items.map((it) => ({
          product_id: it.product_id,
          ordered_qty: parseInt(it.ordered_qty, 10),
          purchase_unit_cost: it.purchase_unit_cost,
          repack_required: !!it.repack_required,
        })),
      };
      let res;
      if (isEdit) {
        res = await api.put(`/purchases/${id}`, payload);
      } else {
        res = await api.post("/purchases", payload);
      }
      navigate(`/purchases/${res.data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (user && user.role !== "OWNER") {
    return (
      <div className="app-shell">
        <AppTopBar active="purchase" />
        <main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <AppTopBar active="purchase" />
      <main className="content">
        <Link to="/purchases" className="back-link" data-testid="back-to-list">← Kembali ke daftar</Link>
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}

        <section className="panel" data-testid="purchase-form-panel">
          <h2 className="panel-title">{isEdit ? `Edit Purchase ${purchaseNo}` : "Buat Purchase Baru"}</h2>
          <form onSubmit={submit}>
            <div className="grid-2">
              {isEdit && (
                <div>
                  <label className="field-label">Purchase No</label>
                  <input className="field-input" value={purchaseNo} disabled data-testid="purchase-no-input" />
                </div>
              )}
              <div>
                <label className="field-label">Tanggal</label>
                <input className="field-input" type="date" value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)} disabled={locked} data-testid="purchase-date-input" />
              </div>
              <div>
                <label className="field-label">Supplier</label>
                <select className="field-input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} disabled={locked} data-testid="purchase-supplier-select" required>
                  <option value="">-- pilih Supplier --</option>
                  {activeSuppliers.map((s) => <option key={s.id} value={s.id}>{s.supplier_code} · {s.name}{s.status !== "ACTIVE" ? " (INACTIVE)" : ""}</option>)}
                </select>
                {!locked && suppliers.filter((s) => s.status === "ACTIVE").length === 0 && <div className="hint" style={{ marginTop: 4 }}>Belum ada Supplier ACTIVE. <Link to="/suppliers">Buat di Master Supplier</Link>.</div>}
              </div>
              <div>
                <label className="field-label">Ongkir Masuk (Rp)</label>
                <input className="field-input" type="number" min="0" step="0.01" value={shipping}
                  onChange={(e) => setShipping(e.target.value)} disabled={locked} data-testid="inbound-shipping-input" />
              </div>
            </div>

            <h3 className="sub-title">Item SKU</h3>
            <div className="table-scroll">
              <table className="data-table" data-testid="items-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 240 }}>SKU (ACTIVE)</th>
                    <th>Qty</th>
                    <th>Harga Beli / Unit</th>
                    <th>Repack</th>
                     <th>Subtotal</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} data-testid={`item-row-${idx}`}>
                      <td>
                        <SkuCombobox products={products} value={it.product_id} disabled={locked}
                          index={idx} onChange={(pid) => setItem(idx, "product_id", pid)} />
                      </td>
                      <td>
                        <input className="field-input narrow" type="number" min="1" value={it.ordered_qty} required disabled={locked}
                          onChange={(e) => setItem(idx, "ordered_qty", e.target.value)} data-testid={`item-qty-${idx}`} />
                      </td>
                      <td>
                        <input className="field-input narrow" type="text" inputMode="decimal" value={it.purchase_unit_cost} required disabled={locked}
                          onChange={(e) => setItem(idx, "purchase_unit_cost", normalizePastedUnitCost(e.target.value))} data-testid={`item-cost-${idx}`} />
                      </td>
                      <td><label style={{display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap"}}><input type="checkbox" checked={!!it.repack_required} disabled={locked} onChange={(e)=>setItem(idx,"repack_required",e.target.checked)} data-testid={`item-repack-${idx}`} />{it.repack_required?"Repack":"Tidak Repack"}</label></td>
                       <td className="mono" data-testid={`item-subtotal-${idx}`}>{formatIDR(rowSubtotal(it))}</td>
                      <td>
                        {items.length > 1 && !locked && (
                          <button type="button" className="btn-mini danger" onClick={() => removeRow(idx)} data-testid={`remove-item-${idx}`}>
                            Hapus
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!locked && (
              <button type="button" className="btn-ghost mt" onClick={addRow} data-testid="add-item-button">+ Tambah SKU</button>
            )}

            <div className="total-row" data-testid="form-total">Total (estimasi): <strong>{formatIDR(total)}</strong></div>

            {!locked && (
              <div className="form-actions">
                <button className="btn-primary" type="submit" disabled={loading || !supplierId} data-testid="save-purchase-button">
                  {loading ? "Menyimpan..." : isEdit ? "Simpan Perubahan (DRAFT)" : "Simpan sebagai DRAFT"}
                </button>
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}
